import { t } from "../../features/store-language/i18n.ts";
import React, { useState, useEffect } from "react";
import { Spin, Image, Space } from "antd";
import { ImageProps } from "antd/es/image";
import axios from "axios";
import placeholderImage from "./images/loading.png";
import "./AuthenticatedImage.css";
import defaultImg from "../../defaultImage.png";
import { getLocalBaseUrl } from "../../utils/localUrl.ts";
import { HttpService } from "../../utils/DesigningHttpService.js";
import { subscribeAgencySession } from "../../utils/agencySession.ts";

const mediaHttp = new HttpService();
const orderMediaCache = new Map<string, Promise<Blob>>();

function orderMediaBlob(orderId: string, mediaId: string): Promise<Blob> {
  const key = `${orderId}:${mediaId}`;
  const cached = orderMediaCache.get(key);
  if (cached) return cached;
  const request = mediaHttp.getResource(`/catalog/orders/${orderId}/media/${mediaId}`, undefined, "blob")
    .then(function (blob: Blob) {
      if (!blob.type.startsWith("image/")) throw new Error(t("تصویر سفارش معتبر نیست."));
      return blob;
    }).catch(function (error) {
      orderMediaCache.delete(key);
      throw error;
    });
  orderMediaCache.set(key, request);
  return request;
}

subscribeAgencySession(function () {
  // Authenticated order media must never be retained across account or agency changes.
  orderMediaCache.clear();
});

interface AuthenticatedImageProps extends Omit<ImageProps, "src"> {
  fileId?: string | number | null;
  previewSrc?: string;
  quality?: string;
  thumbPreviewMode?: boolean;
  disablePreview?: boolean;
  originalImage?: boolean;
  /** Request a stored thumbnail with this height from the API (not CSS display size). */
  thumbnailHeight?: number;
}

interface AuthenticatedImagePlaceholderProps {
  animated?: boolean;
}

// Placeholder image component for skeleton and unavailable product media.
export const AuthenticatedImagePlaceholder = ({
  animated = true,
}: AuthenticatedImagePlaceholderProps) => (
  <div
    className={`authenticated-image-placeholder${animated ? "" : " is-static"}`}
  >
    <img
      src={placeholderImage}
      alt=""
      aria-hidden="true"
      className="placeholder-image"
    />
  </div>
);

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = React.memo(
  ({
    fileId,
    style,
    onError,
    alt,
    className,
    fallback,
    placeholder,
    rootClassName,
    width,
    quality,
    thumbPreviewMode,
    height,
    disablePreview,
    originalImage,
    thumbnailHeight,
  }: AuthenticatedImageProps) => {
    const [imageUrl, setImageUrl] = useState<string>("");
    const [previewUrl, setPreviewUrl] = useState<string>("");
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [isPreviewVisible, setIsPreviewVisible] = useState(false);

    useEffect(() => {
      setImageLoaded(false);
      setImageError(false);
      setPreviewUrl("");
      setIsPreviewVisible(false);

      if (!fileId) {
        setImageUrl(defaultImg);
        return;
      }

      const baseUrl = getLocalBaseUrl(process.env.REACT_APP_Server_Url);

      const orderMedia = /^store-order:([1-9][0-9]*):([1-9][0-9]*)$/.exec(String(fileId));
      if (orderMedia) {
        let active = true;
        let objectUrl = "";
        setImageUrl("");
        orderMediaBlob(orderMedia[1], orderMedia[2]).then(function (blob) {
          if (!active) return;
          objectUrl = URL.createObjectURL(blob);
          setImageError(false);
          setImageLoaded(false);
          setImageUrl(objectUrl);
        }).catch(function () {
          if (active) { setImageError(true); setImageLoaded(true); }
        });
        return function () { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
      }

      let url = `${baseUrl}/file/thumbnail?fileId=${fileId}`;

      if (originalImage) {
        url = `${baseUrl}/file?fileId=${fileId}`;
      } else if (thumbnailHeight) {
        url += `&height=${thumbnailHeight}`;
      }
      if (/^store:[1-9][0-9]*$/.test(String(fileId))) {
        url = `${baseUrl}/catalog/media/${String(fileId).slice(6)}`;
      }
      setImageUrl(url);
    }, [fileId, originalImage, thumbnailHeight]);

    useEffect(() => {
      return function () {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      };
    }, [previewUrl]);

    const fetchPreviewImage = async () => {
      if (/^store-order:/.test(String(fileId))) return;
      if (isPreviewLoading || previewUrl) return;

      setIsPreviewLoading(true);
      try {
        const token = localStorage.getItem("jwt");
        const baseUrl = getLocalBaseUrl(process.env.REACT_APP_Server_Url);
        const url = /^store:[1-9][0-9]*$/.test(String(fileId))
          ? `${baseUrl}/catalog/media/${String(fileId).slice(6)}`
          : thumbPreviewMode
          ? `${baseUrl}/file/thumbnail?fileId=${fileId}&height=1000`
          : `${baseUrl}/file?fileId=${fileId}`;

        const response = /^store:/.test(String(fileId))
          ? await mediaHttp.getblob(`/catalog/media/${String(fileId).slice(6)}`)
          : await axios.get(url, {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 200) {
          const blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });
          setPreviewUrl(URL.createObjectURL(blob));
        }
      } catch (error) {
        console.error("Error loading preview image:", error);
      } finally {
        setIsPreviewLoading(false);
      }
    };

    const handlePreview = async () => {
      if (!previewUrl) {
        await fetchPreviewImage();
      }
    };

    const previewConfig = {
      src: previewUrl || imageUrl,
      maskClassName: "customize-mask",
      mask: (
        <Space direction="vertical" align="center">
          {isPreviewLoading && <Spin size="large" tip={t("در حال دریافت پیش‌نمایش")} />}
        </Space>
      ),
      onVisibleChange: (visible) => {
        setIsPreviewVisible(visible);
        if (visible) {
          handlePreview();
        }
      },
    };

    const handleImageLoad = () => {
      setImageLoaded(true);
    };

    const handleImageError = (
      e: React.SyntheticEvent<HTMLImageElement, Event>,
    ) => {
      setImageError(true);
      setImageLoaded(true);
      setImageUrl(defaultImg);

      if (onError) {
        onError(e);
      }
    };

    // Show skeleton while loading or if image hasn't loaded yet
    // Show skeleton if: we have a valid fileId but image hasn't loaded yet
    const hasValidFileId =
      fileId !== null &&
      fileId !== undefined &&
      fileId !== "" &&
      String(fileId).trim() !== "";
    const showSkeleton = hasValidFileId && !imageLoaded && !imageError;

    if (imageError && /^store(?:-order)?:/.test(String(fileId))) {
      return (
        <div
          className={`authenticated-image-unavailable ${className || ""}`}
          role="img"
          aria-label={alt || t("تصویر کالا")}
          style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", ...style }}
        >
          <AuthenticatedImagePlaceholder animated={false} />
        </div>
      );
    }

    return (
      <>
        {isPreviewVisible && isPreviewLoading && (
          <div
            className="authenticated-image-preview-loading-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              pointerEvents: "auto",
            }}
          >
            <Spin size="large" tip={t("در حال دریافت پیش‌نمایش")} />
          </div>
        )}
        <div
          style={{
            position: "relative",
            height: "100%",
            ...style,
            // Contain stacking; never let caller z-index escape above header menus.
            zIndex: 0,
          }}
        >
          {showSkeleton && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f8f9fa",
              }}
            >
              <AuthenticatedImagePlaceholder />
            </div>
          )}
          {imageUrl && (
            <>
              {disablePreview ? (
                <img
                  src={imageUrl}
                  className={className}
                  alt={alt}
                  style={{
                    backgroundColor: "transparent",
                    mixBlendMode: "normal",
                    opacity: imageLoaded ? 1 : 0,
                    transition: "opacity 0.3s ease-in-out",
                    // Avoid elevated z-index when loaded — it can paint above header menus.
                    ...(imageLoaded
                      ? { pointerEvents: "auto" as const }
                      : {
                          position: "absolute" as const,
                          zIndex: 0,
                          pointerEvents: "none" as const,
                        }),
                    ...style,
                    ...(imageLoaded ? { zIndex: "auto" as const } : null),
                  }}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  ref={(img) => {
                    // Check if image is already loaded (cached) - use setTimeout to ensure skeleton shows briefly
                    if (img && img.complete && !imageLoaded && !imageError) {
                      setTimeout(() => {
                        handleImageLoad();
                      }, 100);
                    }
                  }}
                />
              ) : (
                <Image
                  key={fileId}
                  style={{
                    height: height ?? 40,
                    opacity: imageLoaded ? 1 : 0,
                    transition: "opacity 0.3s ease-in-out",
                    // Avoid elevated z-index when loaded — it can paint above header menus.
                    ...(imageLoaded
                      ? { pointerEvents: "auto" as const }
                      : {
                          position: "absolute" as const,
                          zIndex: 0,
                          pointerEvents: "none" as const,
                        }),
                    ...style,
                    ...(imageLoaded ? { zIndex: "auto" as const } : null),
                  }}
                  preview={previewConfig}
                  onError={handleImageError}
                  onLoad={handleImageLoad}
                  src={imageUrl}
                  alt={alt}
                  className={className}
                  fallback={fallback}
                  placeholder={placeholder}
                  rootClassName={rootClassName}
                />
              )}
            </>
          )}
        </div>
      </>
    );
  },
);

export default AuthenticatedImage;
